import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Package, Phone, Info, Briefcase, Home } from "lucide-react";
import SendtooLogo from "@/components/shared/SendtooLogo";

const navLinks = [
  { to: "/", label: "Home", icon: Home },
  { to: "/about", label: "About", icon: Info },
  { to: "/services", label: "Services", icon: Briefcase },
  { to: "/track", label: "Track", icon: Package },
  { to: "/contact", label: "Contact", icon: Phone },
];

export default function PublicLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white font-body">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <SendtooLogo to="/" size="lg" />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    location.pathname === link.to
                      ? "text-brand-500 bg-brand-50"
                      : "text-slate-600 hover:text-brand-500 hover:bg-slate-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/auth/login"
                data-testid="nav-login-btn"
                className="px-5 py-2 text-sm font-semibold text-brand-500 border-2 border-brand-500 rounded-sm hover:bg-brand-50 transition-colors uppercase tracking-wide"
              >
                Login
              </Link>
              <Link
                to="/auth/register"
                data-testid="nav-register-btn"
                className="px-5 py-2 text-sm font-semibold text-white bg-brand-500 rounded-sm hover:bg-brand-600 transition-colors uppercase tracking-wide"
              >
                Sign Up
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-sm text-slate-600"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-4 pb-4 pt-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-3 py-3 text-sm font-medium text-slate-700 border-b border-slate-50"
                onClick={() => setMenuOpen(false)}
              >
                <link.icon className="w-4 h-4 text-brand-500" />
                {link.label}
              </Link>
            ))}
            <div className="flex gap-3 mt-4">
              <Link to="/auth/login" className="flex-1 text-center py-2 text-sm font-semibold text-brand-500 border-2 border-brand-500 rounded-sm uppercase">Login</Link>
              <Link to="/auth/register" className="flex-1 text-center py-2 text-sm font-semibold text-white bg-brand-500 rounded-sm uppercase">Sign Up</Link>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0A183D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <SendtooLogo size="xl" className="mb-4" />
              <p className="text-slate-400 text-sm leading-relaxed">
                The Reliable Kinetic Network. Moving business, not just boxes. Fast, secure, and transparent logistics for modern India.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-slate-300">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {navLinks.map(l => <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-slate-300">Portals</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Customer Login</Link></li>
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Agent Login</Link></li>
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Admin Panel</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-10 pt-6 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Sendtoo Logistics. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
